import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useOrganizationStore } from '@/core/stores/organization.store';
import { templatesService } from '../api/templates.service';
import { FORM_KEYS, TEMPLATE_KEYS } from './queryKeys';
import type { CreateTemplatePayload, UpdateTemplatePayload } from '../types';

export function useCreateTemplate() {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (data: CreateTemplatePayload) => templatesService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.byOrg(orgId ?? '') });
    },
  });
}

export function useUpdateTemplate(templateId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (data: UpdateTemplatePayload) => templatesService.update(templateId, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.detail(orgId ?? '', templateId) });
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.byOrg(orgId ?? '') });
    },
  });
}

export function useDeleteTemplate(templateId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: () => templatesService.delete(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.byOrg(orgId ?? '') });
    },
  });
}

export function useDuplicateTemplate(templateId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: (title?: string) => templatesService.duplicate(templateId, title),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: TEMPLATE_KEYS.byOrg(orgId ?? '') });
    },
  });
}

/** Instantiates a Draft form from the template — invalidates the forms list, not the templates list. */
export function useInstantiateTemplate(templateId: string) {
  const queryClient = useQueryClient();
  const orgId = useOrganizationStore((s) => s.activeOrganizationId);

  return useMutation({
    mutationFn: () => templatesService.instantiate(templateId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: FORM_KEYS.byOrg(orgId ?? '') });
    },
  });
}
